import { Baby, Car, Users, Video } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SalonService } from '../api/use-marketplace';

/**
 * Renders subtle pill badges next to a service name on the salon detail
 * page when the service supports MOBILE and/or ONLINE delivery. Returns
 * null when there are no badges to show so the row stays clean for
 * fixed-location-only services (the common case for the first wave).
 */

interface ServiceModeBadgesProps {
  service: SalonService;
}

export function ServiceModeBadges({ service }: ServiceModeBadgesProps) {
  const { t } = useTranslation();

  const badges: Array<{ key: string; icon: typeof Car; label: string }> = [];
  if (service.mobileEligible) {
    badges.push({
      key: 'mobile',
      icon: Car,
      label: t('salon.modes.mobile'),
    });
  }
  if (service.onlineEligible) {
    badges.push({
      key: 'online',
      icon: Video,
      label: t('salon.modes.online'),
    });
  }
  // Audience flags (V92): who the service is aimed at; empty = everyone (no badge).
  for (const a of service.audiences ?? []) {
    const key = a.toLowerCase();
    badges.push({
      key: 'aud-' + key,
      icon: key === 'kids' ? Baby : Users,
      label: t('salon.audience.' + key),
    });
  }
  if (badges.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {badges.map(({ key, icon: Icon, label }) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-primary/10 text-primary"
        >
          <Icon className="w-3 h-3" aria-hidden="true" />
          {label}
        </span>
      ))}
    </div>
  );
}
